/**
 * Итоговый шаг мастера подключения (KAN-601).
 */
import { Button } from '@/components/ui/button';
import { DETECTED_LABELS, SCHEME_LABELS } from '@/lib/telegramConnect';
import type {
  ArchiveDetected,
  ConnectScheme,
  ConnectSelfMessage,
  TelegramAccount,
} from '@/types/telegramAccount';

/** Ответ @SpamBot: limited true | false | null (проверка не выполнена) и text. */
function describeSpam(spam: Record<string, unknown>): {
  text: string;
  warn: boolean;
} {
  const extra = typeof spam.text === 'string' ? spam.text.trim() : '';
  const join = (head: string) => (extra ? `${head}. ${extra}` : head);
  if (spam.limited === true) {
    return { text: join('Аккаунт ограничен по данным @SpamBot'), warn: true };
  }
  if (spam.limited === false) {
    return { text: 'Ограничений не найдено', warn: false };
  }
  return { text: join('Проверка @SpamBot не выполнена'), warn: false };
}

interface Props {
  account: TelegramAccount;
  scheme: ConnectScheme;
  spam?: Record<string, unknown> | null;
  /** KAN-659: результат автоматического сообщения себе после подключения. */
  selfMessage?: ConnectSelfMessage | null;
  /** Предупреждение импорта (KAN-675 несколько аккаунтов в tdata, KAN-677 сессии и JSON). */
  warning?: string | null;
  /** KAN-677: что сервер нашёл в архиве продавца (только схема archive). */
  detected?: ArchiveDetected | null;
  onDone: () => void;
}

export function ConnectResultStep({
  account,
  scheme,
  spam,
  selfMessage,
  warning,
  detected,
  onDone,
}: Props): JSX.Element {
  const spamView = spam ? describeSpam(spam) : null;
  const selfView = selfMessage?.detail ? selfMessage : null;
  const warningText = warning?.trim() || null;
  const detectedText = detected ? DETECTED_LABELS[detected] : null;
  return (
    <div className="space-y-4" data-testid="connect-result">
      <p className="font-medium text-green-700">Аккаунт подключён</p>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">Метка</dt>
        <dd data-testid="connect-result-label">
          {account.label || account.username || account.account_id}
        </dd>
        <dt className="text-gray-500">Телефон</dt>
        <dd data-testid="connect-result-phone">{account.phone || '-'}</dd>
        <dt className="text-gray-500">Способ</dt>
        <dd>{SCHEME_LABELS[scheme]}</dd>
        {detectedText && (
          <>
            <dt className="text-gray-500">Формат</dt>
            <dd data-testid="connect-result-detected">{detectedText}</dd>
          </>
        )}
        {spamView && (
          <>
            <dt className="text-gray-500">@SpamBot</dt>
            <dd
              data-testid="connect-result-spam"
              className={spamView.warn ? 'text-amber-700' : undefined}
            >
              {spamView.text}
            </dd>
          </>
        )}
        {selfView && (
          <>
            <dt className="text-gray-500">Сообщение себе</dt>
            <dd
              data-testid="connect-result-self-message"
              className={selfView.status === 'skipped' ? 'text-amber-700' : undefined}
            >
              {selfView.detail}
            </dd>
          </>
        )}
        {warningText && (
          <>
            <dt className="text-gray-500">Предупреждение</dt>
            <dd data-testid="connect-result-warning" className="text-amber-700">
              {warningText}
            </dd>
          </>
        )}
      </dl>
      <div className="flex justify-end">
        <Button data-testid="connect-done-button" onClick={onDone}>
          Готово
        </Button>
      </div>
    </div>
  );
}
